/**
 * Sprint 23.6 v2 — AgentProfileTabs.
 *
 * Sticky 8-tab bar below the hero (Q2). Tab order is locked:
 *   Priorities · Tasks · Routines · Pages · Memories · Files · Activity · Settings
 *
 * Active tab is driven by the URL hash (Q20) so back/forward + Cmd-K all share
 * one source of truth. No default tab — the hero IS the landing view.
 *
 * Visual contract (Hero section 7): flat text tabs, no icons, gap 14, font 12,
 * each tab padding 10px 0; active tab gets a 2px text-primary bottom border;
 * counts are plain tertiary spans (no badge pills). `position: sticky; top: 0`
 * pins the bar once the hero scrolls past.
 */

import { type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router";
import { TabBar } from "../shell/TabBar";

export const AGENT_PROFILE_TABS = [
  "tasks",
  "routines",
  "pages",
  "memories",
  "files",
  "activity",
  "settings",
] as const;
export type AgentProfileTab = (typeof AGENT_PROFILE_TABS)[number];

export function isAgentProfileTab(value: string): value is AgentProfileTab {
  return (AGENT_PROFILE_TABS as readonly string[]).includes(value);
}

/** The tab shown when the URL carries no (or an unknown) hash. */
export const DEFAULT_AGENT_PROFILE_TAB: AgentProfileTab = "tasks";

/**
 * Decode the active tab from the URL hash. Defaults to the first tab
 * (Tasks) so landing on an agent opens real content rather than an empty
 * area below the hero. Strips any `?query` suffix so deep-links like
 * `#activity?highlight=<id>` still resolve to their tab.
 */
export function readActiveTab(hash: string): AgentProfileTab {
  const raw = (hash.startsWith("#") ? hash.slice(1) : hash).split(/[?&]/)[0] ?? "";
  return isAgentProfileTab(raw) ? raw : DEFAULT_AGENT_PROFILE_TAB;
}

const TAB_LABELS: Record<AgentProfileTab, string> = {
  tasks: "Tasks",
  routines: "Routines",
  pages: "Pages",
  memories: "Memories",
  files: "Files",
  activity: "Activity",
  settings: "Settings",
};

export interface AgentProfileTabsProps {
  /** Optional pre-fetched counts so the bar can show "Tasks 12" etc.
   *  Keys must match AgentProfileTab; absent keys / 0 render with no count. */
  counts?: Partial<Record<AgentProfileTab, number>>;
}

export function AgentProfileTabs({ counts }: AgentProfileTabsProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const active = readActiveTab(location.hash);

  return (
    <TabBar
      tabs={AGENT_PROFILE_TABS.map((tab) => ({
        key: tab,
        label: TAB_LABELS[tab],
        count: counts?.[tab],
      }))}
      active={active}
      onSelect={(key) => navigate({ hash: `#${key}` }, { replace: false })}
      barStyle={cardBleedBar}
      ariaLabel="Agent profile sections"
    />
  );
}

// The bar sits inside the hero container card (`--color-background-primary`), so
// it matches that surface and reads as a clean underline bar — not a contrasting
// strip. Full-bleed the hero card's 20px horizontal padding so the pinned bar
// (and its underline rule) span the whole card width, while the tabs themselves
// stay aligned with the content via the matching inline padding.
const cardBleedBar: CSSProperties = {
  background: "var(--color-background-primary)",
  borderBottom: "0.5px solid var(--color-border-secondary)",
  marginLeft: -20,
  marginRight: -20,
  paddingLeft: 20,
  paddingRight: 20,
};
